import React, { useEffect, useMemo } from "react";
import { useUnifiedService } from "../../../utilities/contexts/UnifiedServiceContext";
import { ImageUpload } from "../../../utilities/types/unified-services";
import { useCameraAvailability } from "../../../utilities/hooks/useCameraAvailability";
import GarmentMeasurementUpload from "./GarmentMeasurementUpload";
import OptimizedBodyMeasurementCamera from "./OptimizedBodyMeasurementCamera";

const UnifiedUploadStep: React.FC = () => {
  const { serviceInterface, serviceConfig, bodyMeasurement, autoTagging, visualSearch, garmentMeasurement } = useUnifiedService();
  const { setCanProceedToNextStep } = serviceInterface;
  const { isAvailable: isCameraAvailable, isChecking: isCameraChecking } = useCameraAvailability();

  const { upload } = serviceConfig;
  const isAutoTagging = serviceConfig.serviceId === "auto-tagging";
  const isVisualSearch = serviceConfig.serviceId === "visual-search";
  const isGarmentMeasurement = serviceConfig.serviceId === "garment-measurement";

  const frontImage = useMemo(() => {
    if (isAutoTagging) return autoTagging.image;
    if (isVisualSearch) return visualSearch.image;
    if (isGarmentMeasurement) return garmentMeasurement.image;
    return bodyMeasurement.frontImage;
  }, [isAutoTagging, isVisualSearch, isGarmentMeasurement, autoTagging.image, visualSearch.image, garmentMeasurement.image, bodyMeasurement.frontImage]);

  const sideImage = useMemo(
    () => (isAutoTagging || isVisualSearch || isGarmentMeasurement ? { isValid: true, preview: null, error: null } : bodyMeasurement.sideImage),
    [isAutoTagging, isVisualSearch, isGarmentMeasurement, bodyMeasurement.sideImage]
  );

  useEffect(() => {
    if (isGarmentMeasurement) {
      return;
    }

    if (upload?.photoCount === 2) {
      setCanProceedToNextStep(frontImage.isValid && sideImage.isValid);
    } else if (upload?.photoCount === 1) {
      setCanProceedToNextStep(frontImage.isValid);
    }
  }, [frontImage, sideImage, upload?.photoCount, serviceConfig.serviceId, isGarmentMeasurement]);

  const validateImage = (file: File): { isValid: boolean; error?: string } => {
    const { processing } = serviceConfig;
    const { imageValidation } = processing;

    if (!imageValidation.allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: `Invalid file type. Allowed: ${imageValidation.allowedTypes.join(", ")}`,
      };
    }

    if (file.size > imageValidation.maxSizeKB * 1024) {
      return {
        isValid: false,
        error: `File too large. Max size: ${(imageValidation.maxSizeKB / 1024).toFixed(1)}MB`,
      };
    }

    return { isValid: true };
  };

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleImageUpload = async (file: File, imageType: "front" | "side") => {
    const validation = validateImage(file);

    const newImageState: ImageUpload = {
      file,
      base64: null,
      preview: URL.createObjectURL(file),
      isValid: validation.isValid,
      error: validation.error,
      blurredBase64: null,
    };

    if (validation.isValid) {
      try {
        const base64 = await convertToBase64(file);
        newImageState.base64 = base64;
      } catch (error) {
        newImageState.isValid = false;
        newImageState.error = "Failed to process image";
      }
    }
    if (isAutoTagging) {
      autoTagging.setImageFile(file);
    } else if (isVisualSearch) {
      visualSearch.setImage(newImageState);
    } else if (isGarmentMeasurement) {
      garmentMeasurement.setImage(newImageState, 0);
    } else {
      if (imageType === "front") {
        bodyMeasurement.setFrontImage(newImageState);
      } else {
        bodyMeasurement.setSideImage(newImageState);
      }
    }
  };

  const handleFrontImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleImageUpload(file, "front");
    }
  };

  const handleSideImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleImageUpload(file, "side");
    }
  };

  const handleBackToCamera = () => {
    bodyMeasurement.setCameraOptedOut(false);
  };

  if (isGarmentMeasurement) {
    return <GarmentMeasurementUpload />;
  }

  if (serviceConfig.camera?.enabled && serviceConfig.serviceId === "body-measurement" && !bodyMeasurement.cameraOptedOut && !isCameraChecking && isCameraAvailable) {
    return <OptimizedBodyMeasurementCamera />;
  }

  if (isCameraChecking && serviceConfig.camera?.enabled && serviceConfig.serviceId === "body-measurement" && !bodyMeasurement.cameraOptedOut) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-8 h-8 mb-4 border-2 border-gray-500 rounded-full border-t-transparent animate-spin"></div>
        <p className="text-gray-600">Checking camera availability...</p>
      </div>
    );
  }

  if (upload?.photoCount === 1) {
    return (
      <>
        <div className="flex flex-col items-center w-full gap-4">
          <div className="text-center">
            <h2 className="mb-1 text-2xl font-medium text-secondary">Upload Image</h2>
            <p className="text-gray-600">Upload a clear image for best results</p>
          </div>

          <div className="flex flex-col items-center w-full max-w-[18rem] gap-4 p-4 bg-white border border-gray-200 shadow-sm rounded-xl">
            <div className="flex items-center justify-center w-full overflow-hidden border-2 border-gray-200 rounded-lg h-80">
              {frontImage.preview ? (
                <img src={frontImage.preview} alt="Uploaded" className="object-contain w-full h-full" />
              ) : (
                <img src={upload?.placeholderImages?.[0] || "/images/default/default-auto-tagging.png"} alt="Upload placeholder" className="object-contain object-center w-fit" />
              )}
            </div>

            <div className="flex flex-col w-full gap-4">
              <div className="flex flex-col gap-2">
                <button className="flex items-center justify-center gap-2 font-medium transition-all duration-300 bg-gray-100 rounded-lg hover:bg-gray-200">
                  <label htmlFor="imageFile" className="flex items-center justify-center w-full gap-2 px-6 py-3 cursor-pointer ">
                    <span>{frontImage.isValid ? "Change Image" : "Upload from Gallery"}</span>
                  </label>
                </button>

                {frontImage.error && <div className="p-2 text-sm text-center text-red-500 rounded-md bg-red-50">{frontImage.error}</div>}

                <input id="imageFile" type="file" className="hidden" onChange={handleFrontImageChange} accept="image/*" />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col items-center w-full gap-4">
        <div className="text-center">
          <h2 className="mb-1 text-2xl font-medium text-secondary">Upload Photos</h2>
          <p className="text-gray-600">Please upload both front view and side view photos</p>

          {serviceConfig.camera?.enabled && !isCameraChecking && !isCameraAvailable && !bodyMeasurement.cameraOptedOut && (
            <div className="mt-3 text-sm text-gray-500">Camera not available on this device</div>
          )}

          {bodyMeasurement.cameraOptedOut && serviceConfig.camera?.enabled && !isCameraChecking && isCameraAvailable && (
            <div className="mt-4">
              <button onClick={handleBackToCamera} className="px-4 py-2 text-sm text-gray-600 transition-colors border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100">
                Use Camera Instead
              </button>
            </div>
          )}
        </div>

        <div className="grid w-full grid-cols-1 gap-6 place-items-center 2xl:grid-cols-2 2xl:gap-8 2xl:*:min-h-full">
          <div className="flex flex-col items-center gap-4 p-6 bg-white border border-gray-200 rounded-lg shadow-sm md:flex-row w-fit md:justify-center xl:justify-between">
            <div className="w-[9.375rem] h-[17.375rem] border-2 rounded-lg border-gray-200 overflow-hidden bg-gray-50">
              {frontImage.preview ? (
                <img src={frontImage.preview} alt="Front View" className="object-contain w-full h-full" />
              ) : (
                <img src={upload?.placeholderImages?.[0] || "/images/guide/front-pose.png"} alt="temp-front-body-measurment" className="object-contain w-full h-full" />
              )}
            </div>
            <div className="flex flex-col justify-between gap-4 min-w-[15.125rem] md:max-w-[25rem]">
              <div className="flex flex-col w-full gap-4">
                <div className="flex gap-2 font-medium text-secondary">
                  <span className="grid text-sm font-medium rounded-full bg-primaryLight min-w-6 size-6 place-content-center text-primary">1</span>
                  <h3 className="text-lg">{upload?.photoDescriptions?.[0]?.title || "Front View Image"}</h3>
                </div>
                <ul className="space-y-2 text-sm text-gray-600 list-disc ps-6">
                  {upload?.photoDescriptions?.[0]?.instructions?.map((instruction, index) => <li key={index}>{instruction}</li>) || [
                    "Ensure the full body is visible.",
                    "Face the camera directly.",
                    "Stand straight; don't slouch.",
                    "Wear fitted clothing, not loose.",
                    "Ensure no shoes are included in the image.",
                    "Keep each image under 3MB.",
                  ]}
                </ul>
              </div>

              <div className="flex flex-col w-full gap-2">
                <button className="flex items-center justify-center gap-2 font-medium transition-all duration-300 rounded-lg btn-primary hover:shadow-md">
                  <label htmlFor="frontImageFile" className="flex items-center justify-center w-full gap-2 px-4 py-3 cursor-pointer ">
                    <span>{frontImage.isValid ? "Change Image" : "Upload Image"}</span>
                  </label>
                </button>

                {frontImage.error && <div className="p-2 text-sm text-red-500 rounded-md bg-red-50">{frontImage.error}</div>}

                <input id="frontImageFile" type="file" className="hidden" onChange={handleFrontImageChange} accept="image/*" />
              </div>
            </div>
          </div>
          {!isAutoTagging && !isVisualSearch && !isGarmentMeasurement && (
            <div className="flex flex-col items-center gap-4 p-6 bg-white border border-gray-200 rounded-lg shadow-sm md:flex-row w-fit md:justify-center xl:justify-between">
              <div className="w-[9.375rem] h-[17.375rem] border-2 rounded-lg border-gray-200 overflow-hidden bg-gray-50">
                {sideImage.preview ? (
                  <img src={sideImage.preview} alt="Side View" className="object-contain w-full h-full" />
                ) : (
                  <img
                    src={upload?.placeholderImages?.[1] || "/assets/image/free-trial/body-measurment/temp-side-body-measurment.png"}
                    alt="temp-side-body-measurment"
                    className="object-contain w-full h-full"
                  />
                )}
              </div>
              <div className="flex flex-col justify-between gap-4 min-w-[15.125rem] md:max-w-[25rem]">
                <div className="flex flex-col w-full gap-4">
                  <div className="flex gap-2 font-medium text-secondary">
                    <span className="grid text-sm font-medium rounded-full bg-primaryLight size-6 place-content-center text-primary">2</span>
                    <h3 className="text-lg">{upload?.photoDescriptions?.[1]?.title || "Side View Image"}</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-600 list-disc ps-6">
                    {upload?.photoDescriptions?.[1]?.instructions?.map((instruction, index) => <li key={index}>{instruction}</li>) || [
                      "Ensure the full body is visible.",
                      "Stand in full profile (side view).",
                      "Stand straight; don't slouch.",
                      "Wear fitted clothing, not loose.",
                      "Ensure no shoes are included in the image.",
                      "Keep each image under 3MB.",
                    ]}
                  </ul>
                </div>

                <div className="flex flex-col w-full gap-2">
                  <button className="flex items-center justify-center gap-2 font-medium transition-all duration-300 rounded-lg btn-primary hover:shadow-md">
                    <label htmlFor="sideImageFile" className="flex items-center justify-center w-full gap-2 px-4 py-3 cursor-pointer">
                      <span>{sideImage.isValid ? "Change Image" : "Upload Image"}</span>
                    </label>
                  </button>
                  {sideImage.error && <div className="p-2 text-sm text-red-500 rounded-md bg-red-50">{sideImage.error}</div>}
                  <input id="sideImageFile" type="file" className="hidden" onChange={handleSideImageChange} accept="image/*" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UnifiedUploadStep;
